import { useCallback, useEffect, useRef, useState } from 'react'

import { UndoToastApi, UndoToastEntry } from './use-undo-toast-types'

const DEFAULT_DURATION_MS = 8000

/**
 * Owns a single ephemeral "undo" toast at the top of the stack. Calling
 * `show()` replaces any current toast and starts a fresh expiry timer.
 * `runUndo()` invokes the stored callback and dismisses; `dismiss()`
 * lets the toast expire silently (the action stays applied).
 *
 * @returns {UndoToastApi} the toast controller
 */
const useUndoToast = (): UndoToastApi => {
  const [toast, setToast] = useState<UndoToastEntry | null>(null)
  const expiryHandleReference = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  )

  const clearExpiry = useCallback((): void => {
    if (expiryHandleReference.current !== null) {
      clearTimeout(expiryHandleReference.current)
      expiryHandleReference.current = null
    }
  }, [])

  useEffect(() => {
    return () => {
      clearExpiry()
    }
  }, [clearExpiry])

  const dismiss = useCallback((): void => {
    clearExpiry()
    setToast(null)
  }, [clearExpiry])

  const show = useCallback(
    (message: string, onUndo: () => void): void => {
      clearExpiry()

      const id = `undo_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
      const expiresAt = Date.now() + DEFAULT_DURATION_MS

      setToast({ id, message, onUndo, expiresAt })

      expiryHandleReference.current = setTimeout(() => {
        setToast(null)
      }, DEFAULT_DURATION_MS)
    },
    [clearExpiry],
  )

  const runUndo = useCallback((): void => {
    setToast(previous => {
      if (previous !== null) {
        previous.onUndo()
      }
      return null
    })
    clearExpiry()
  }, [clearExpiry])

  return { toast, show, dismiss, runUndo }
}

export { useUndoToast }
